import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Mono,
  PageHeader,
  Switch,
  Text,
} from "@lagoon/ui";

import { api } from "../api.js";
import { Failure, LoadingPanel, Notice } from "../components/feedback.js";

/**
 * Account-wide preferences.
 *
 * Deploy emails used to default to on for every site ever created, which is
 * how a product teaches people to filter its mail. They are off now, and this
 * is the one place to turn them on everywhere rather than site by site.
 *
 * A site that has made its own choice keeps it: this setting only reaches
 * sites still set to follow the account.
 */
export function Settings(): React.ReactElement {
  const queryClient = useQueryClient();
  /**
   * The choice just made, shown before the server confirms it.
   *
   * The control reads from the `me` query, which does not update until the
   * mutation and its refetch finish, so without this the switch appears not to
   * respond to being pressed.
   */
  const [pending, setPending] = useState<boolean | null>(null);

  const me = useQuery({ queryKey: ["me"], queryFn: api.me });

  const save = useMutation({
    mutationFn: (notifyOnDeploy: boolean) => api.setAccountNotifications(notifyOnDeploy),
    onSuccess: async () => {
      setPending(null);
      // Both: the site panels show what following the account amounts to.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    // Snap back rather than leave the control showing a choice that did not take.
    onError: () => {
      setPending(null);
    },
  });

  if (me.isPending) {
    return <LoadingPanel label="Loading your settings" />;
  }
  if (me.isError) {
    return <Failure error={me.error} fallback="Could not load your settings." />;
  }

  const owner = me.data.owner;
  const checked = pending ?? owner.notifyOnDeploy;

  return (
    <div className="lg:flex lg:max-w-3xl lg:flex-col lg:gap-7">
      <PageHeader
        title="Account settings"
        description={
          <>
            Signed in as <Mono>{owner.email}</Mono>
          </>
        }
      />

      <Card as="section" interactive={false}>
        <div className="lg:flex lg:flex-col lg:gap-5">
          <CardHeader>
            <CardTitle as="h2">Deploy emails</CardTitle>
            <CardDescription>
              Off unless you ask for them. This is the default for every site; a site can still be
              set to always or never on its own Settings tab.
            </CardDescription>
          </CardHeader>

          {save.isError ? <Notice tone="danger">Could not save that.</Notice> : null}

          <div className="lg:flex lg:items-start lg:gap-4">
            <Switch
              id="notify-on-deploy"
              checked={checked}
              onCheckedChange={(next) => {
                setPending(next);
                save.mutate(next);
              }}
            />
            <div className="lg:flex lg:flex-col lg:gap-1">
              {/* The sentence voice: this labels a choice, not a form field. */}
              <Label
                htmlFor="notify-on-deploy"
                className="lg:font-sans lg:text-body-sm lg:normal-case lg:tracking-normal lg:text-foreground"
              >
                Email me when a deploy finishes
              </Label>
              <Text as="span" size="caption" tone="subtle">
                Including when one fails, with the reason. Applies to every site that is following
                your account setting.
              </Text>
            </div>
          </div>

          {checked ? null : (
            <Text size="caption" tone="subtle">
              You will still see every deploy&rsquo;s outcome in the dashboard, and webhooks are
              unaffected: they are configured per site and are not covered by this.
            </Text>
          )}
        </div>
      </Card>
    </div>
  );
}
